import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CLIENTS } from "@/constants/testIds";
import { toast, Toaster } from "sonner";
import { EnrollSequenceButton } from "@/pages/Sequences";
import { US_STATES, formatPhone } from "@/lib/formHelpers";
import ClientAddOnsPanel from "@/components/app/ClientAddOnsPanel";
import FileUpload from "@/components/FileUpload";
import {
  ArrowLeft, Save, Trash2, FileText, Send, Plus, ExternalLink, Download,
  MessageSquare, Phone, Mail, Calendar, X, CheckCircle2, Circle, Image as ImageIcon, ShieldCheck, ListChecks,
} from "lucide-react";

/** Compact brand-assets panel for a client — logo + cover photo. */
function ClientBrandAssets({ clientId }) {
  const [files, setFiles] = useState([]);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/files", {
        params: { ref_type: "client", ref_id: clientId, limit: 20 },
      });
      setFiles(data.files || []);
    } catch { /* noop */ }
  }, [clientId]);
  useEffect(() => { if (clientId) load(); }, [clientId, load]);
  const logos = files.filter((f) => f.purpose === "client_logo");
  const covers = files.filter((f) => f.purpose === "client_cover");
  return (
    <div className="bg-white border border-neutral-200 p-5 mb-4" data-testid="client-brand-assets">
      <div className="flex items-center gap-2 mb-3">
        <ImageIcon className="w-4 h-4 text-neutral-500" />
        <div className="font-semibold text-neutral-900">Brand assets</div>
        <div className="text-xs text-neutral-400 font-mono ml-auto">{files.length} file{files.length===1?"":"s"}</div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="overline mb-2">Brand logo</div>
          {logos[0] ? (
            <AuthedThumb file={logos[0]} />
          ) : (
            <FileUpload purpose="client_logo" refType="client" refId={clientId} label="Upload logo" onUploaded={load} maxMb={5} />
          )}
        </div>
        <div>
          <div className="overline mb-2">Cover photo</div>
          {covers[0] ? (
            <AuthedThumb file={covers[0]} />
          ) : (
            <FileUpload purpose="client_cover" refType="client" refId={clientId} label="Upload cover" onUploaded={load} maxMb={10} />
          )}
        </div>
      </div>
    </div>
  );
}

function AuthedThumb({ file }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoke = null;
    (async () => {
      try {
        const resp = await api.get(`/files/${file.id}/download`, { responseType: "blob" });
        const u = URL.createObjectURL(resp.data); setUrl(u); revoke = u;
      } catch { /* noop */ }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [file.id]);
  return url
    ? <img src={url} alt={file.original_filename} className="w-full h-32 object-contain border border-neutral-200 bg-neutral-50" />
    : <div className="w-full h-32 bg-neutral-100 animate-pulse" />;
}

const STATUS_OPTIONS = ["prospect", "trial", "active", "paused", "churned"];
const PLAN_OPTIONS = [
  { key: "starter", label: "Starter" },
  { key: "front_office", label: "Front Office" },
  { key: "multi_location", label: "Multi-Location" },
  { key: "custom", label: "Custom" },
];
const ACTIVITY_KINDS = [
  { key: "note", label: "Note", icon: MessageSquare },
  { key: "call", label: "Call", icon: Phone },
  { key: "email", label: "Email", icon: Mail },
  { key: "meeting", label: "Meeting", icon: Calendar },
];
const DOC_TYPES = [
  { key: "service_agreement", label: "Service Agreement" },
  { key: "welcome_letter", label: "Welcome Letter" },
  { key: "kickoff_checklist", label: "Kickoff Checklist" },
];

export default function ClientDetail() {
  const { user: me } = useAuth();
  const isAdmin = (me?.role || "") === "admin";
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [activities, setActivities] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [genLoading, setGenLoading] = useState("");
  const [actKind, setActKind] = useState("note");
  const [actBody, setActBody] = useState("");
  const [addingActivity, setAddingActivity] = useState(false);
  const [checklist, setChecklist] = useState({});
  const [checklistSteps, setChecklistSteps] = useState([]);
  const [emailDoc, setEmailDoc] = useState(null);
  const [emailForm, setEmailForm] = useState({ to: "", intro: "" });
  const [emailSending, setEmailSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${id}`);
      setClient(data.client);
      setActivities(data.activities || []);
      setDocuments(data.documents || []);
      const { data: cl } = await api.get(`/clients/${id}/checklist`);
      setChecklistSteps(cl.steps || []);
      setChecklist(cl.checklist || {});
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/clients");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/clients/${id}`, {
        business_name: client.business_name,
        contact_name: client.contact_name,
        email: client.email,
        phone: client.phone,
        industry: client.industry,
        city: client.city,
        state: client.state,
        website_url: client.website_url,
        status: client.status,
        plan_tier: client.plan_tier,
        mrr: Number(client.mrr) || 0,
        notes: client.notes,
      });
      setClient(data);
      toast.success("Saved.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteClient = async () => {
    if (!window.confirm(`Delete "${client.business_name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Client deleted.");
      navigate("/clients");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addActivity = async () => {
    if (!actBody.trim()) { toast.error("Say something first."); return; }
    setAddingActivity(true);
    try {
      const { data } = await api.post(`/clients/${id}/activities`, {
        kind: actKind, body: actBody,
      });
      setActivities([data, ...activities]);
      setActBody("");
      toast.success("Activity logged.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAddingActivity(false); }
  };

  const generateDoc = async (docType) => {
    setGenLoading(docType);
    try {
      const monthlyPrice = client.mrr > 0 ? client.mrr :
        client.plan_tier === "front_office" ? 697 :
        client.plan_tier === "multi_location" ? 997 : 397;
      const { data } = await api.post("/onboarding/generate", {
        client_id: id,
        doc_type: docType,
        plan_tier: client.plan_tier,
        monthly_price: monthlyPrice,
        setup_fee: 0,
      });
      setDocuments([data, ...documents]);
      toast.success(`Generated ${data.label}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setGenLoading(""); }
  };

  const toggleChecklist = async (key) => {
    try {
      const { data } = await api.patch(`/clients/${id}/checklist`, {
        [key]: !checklist[key],
      });
      setChecklist(data.checklist || {});
      // Reload client + activities so status change (live→active) reflects
      const { data: fresh } = await api.get(`/clients/${id}`);
      setClient(fresh.client);
      setActivities(fresh.activities || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openEmailDoc = (doc) => {
    setEmailDoc(doc);
    setEmailForm({ to: client?.email || "", intro: "" });
  };

  const sendEmailDoc = async () => {
    if (!emailDoc) return;
    setEmailSending(true);
    try {
      await api.post(`/onboarding/${emailDoc.id}/email`, {
        to: emailForm.to || null,
        intro: emailForm.intro,
      });
      toast.success(`Sent to ${emailForm.to}`);
      setEmailDoc(null);
      // Refresh so activity + checklist auto-flip appears
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setEmailSending(false); }
  };

  if (loading) return <div className="p-10 text-neutral-500">Loading…</div>;
  if (!client) return null;

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  return (
    <div data-testid={CLIENTS.detailContainer} className="p-10">
      <Toaster position="top-right" richColors />
      <Link to="/clients" className="inline-flex items-center gap-2 text-sm font-medium text-neutral-500 hover:text-black mb-6">
        <ArrowLeft className="w-4 h-4" /> All clients
      </Link>

      <div className="flex items-start justify-between mb-8 gap-6">
        <div>
          <div className="overline mb-2">Client</div>
          <h1 className="font-display text-4xl font-black tracking-tight">{client.business_name}</h1>
          <p className="text-neutral-600 mt-2">{[client.industry, client.city, client.state].filter(Boolean).join(" · ")}</p>
        </div>
        <div className="flex gap-2">
          <EnrollSequenceButton targetId={client.id} targetType="client" onEnrolled={load} />
          <Link to={`/clients/${client.id}/onboarding`} data-testid="client-onboarding-link"
                className="lf-btn-outline flex items-center gap-2">
            <ListChecks className="w-4 h-4" /> Onboarding
          </Link>
          <Link to={`/clients/${client.id}/credentials`} data-testid="client-credentials-link"
                className="lf-btn-outline flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" /> Credentials
          </Link>
          <button data-testid={CLIENTS.saveBtn} onClick={save} disabled={saving} className="lf-btn-primary flex items-center gap-2">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
          </button>
          {isAdmin && (
            <button data-testid={CLIENTS.deleteBtn} onClick={deleteClient} className="lf-btn-outline flex items-center gap-2" style={{ color: "#B91C1C", borderColor: "#B91C1C" }}>
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* LEFT: fields */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-neutral-200 p-6">
            <div className="overline mb-4">Details</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Business name">
                <input value={client.business_name || ""} onChange={(e) => setClient({ ...client, business_name: e.target.value })} className="lf-input" />
              </Field>
              <Field label="Contact name">
                <input value={client.contact_name || ""} onChange={(e) => setClient({ ...client, contact_name: e.target.value })} className="lf-input" />
              </Field>
              <Field label="Email">
                <input type="email" value={client.email || ""} onChange={(e) => setClient({ ...client, email: e.target.value })} className="lf-input" />
              </Field>
              <Field label="Phone">
                <input value={client.phone || ""} onChange={(e) => setClient({ ...client, phone: formatPhone(e.target.value) })} className="lf-input font-mono" placeholder="(555) 123-4567" maxLength={14} />
              </Field>
              <Field label="Industry">
                <input value={client.industry || ""} onChange={(e) => setClient({ ...client, industry: e.target.value })} className="lf-input" />
              </Field>
              <Field label="City / State">
                <div className="grid grid-cols-2 gap-2">
                  <input value={client.city || ""} onChange={(e) => setClient({ ...client, city: e.target.value })} className="lf-input" placeholder="City" />
                  <select value={client.state || ""} onChange={(e) => setClient({ ...client, state: e.target.value })} className="lf-input">
                    <option value="">State —</option>
                    {US_STATES.map(([code, name]) => (
                      <option key={code} value={code}>{code} — {name}</option>
                    ))}
                  </select>
                </div>
              </Field>
              <Field label="Website URL">
                <input value={client.website_url || ""} onChange={(e) => setClient({ ...client, website_url: e.target.value })} className="lf-input" />
              </Field>
              <Field label="Status">
                <select data-testid={CLIENTS.statusSelect} value={client.status} onChange={(e) => setClient({ ...client, status: e.target.value })} className="lf-input">
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Plan">
                <select data-testid={CLIENTS.planSelect} value={client.plan_tier || "starter"} onChange={(e) => setClient({ ...client, plan_tier: e.target.value })} className="lf-input">
                  {PLAN_OPTIONS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </Field>
              <Field label="MRR ($)">
                <input data-testid={CLIENTS.mrrInput} type="number" min="0" value={client.mrr || 0} onChange={(e) => setClient({ ...client, mrr: e.target.value })} className="lf-input" />
              </Field>
              <div className="md:col-span-2">
                <Field label="Notes">
                  <textarea data-testid={CLIENTS.notesInput} value={client.notes || ""} onChange={(e) => setClient({ ...client, notes: e.target.value })} rows={4} className="lf-input" />
                </Field>
              </div>
            </div>
          </div>

          {/* Add-Ons */}
          <ClientBrandAssets clientId={id} />
      <ClientAddOnsPanel clientId={id} />

          {/* Activity */}
          <div className="bg-white border border-neutral-200 p-6">
            <div className="overline mb-4">Activity log</div>
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <select
                data-testid={CLIENTS.activityKind}
                value={actKind}
                onChange={(e) => setActKind(e.target.value)}
                className="lf-input sm:w-40"
              >
                {ACTIVITY_KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
              </select>
              <input
                data-testid={CLIENTS.activityInput}
                value={actBody}
                onChange={(e) => setActBody(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addActivity()}
                placeholder="Called and left a voicemail…"
                className="lf-input flex-1"
              />
              <button
                data-testid={CLIENTS.activityAdd}
                onClick={addActivity}
                disabled={addingActivity}
                className="lf-btn-primary flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> {addingActivity ? "Adding…" : "Log"}
              </button>
            </div>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {activities.length === 0 ? (
                <p className="text-sm text-neutral-500">No activity yet.</p>
              ) : activities.map((a) => {
                const kind = ACTIVITY_KINDS.find((k) => k.key === a.kind) || ACTIVITY_KINDS[0];
                const Icon = kind.icon;
                return (
                  <div key={a.id} className="border-l-2 border-neutral-200 pl-3 py-1">
                    <div className="flex items-center gap-2 text-xs text-neutral-500 mb-1">
                      <Icon className="w-3 h-3" />
                      <span className="uppercase tracking-widest font-semibold">{kind.label}</span>
                      <span>·</span>
                      <span className="font-mono">{new Date(a.created_at).toLocaleString()}</span>
                    </div>
                    <div className="text-sm">{a.body}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* RIGHT: checklist + docs */}
        <div className="space-y-4">
          {/* Onboarding checklist */}
          <div className="bg-white border border-neutral-200 p-6" data-testid="client-onboarding-checklist">
            <div className="overline mb-4">Onboarding checklist</div>
            <div className="space-y-2">
              {checklistSteps.map((step) => {
                const done = !!checklist[step.key];
                const at = checklist[`${step.key}_at`];
                return (
                  <button
                    key={step.key}
                    data-testid={`checklist-step-${step.key}`}
                    onClick={() => toggleChecklist(step.key)}
                    className={`w-full text-left flex items-start gap-3 px-3 py-2.5 border transition ${done ? "border-green-200 bg-green-50" : "border-neutral-200 hover:border-black hover:bg-neutral-50"}`}
                  >
                    {done ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                    ) : (
                      <Circle className="w-5 h-5 text-neutral-300 shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <div className={`text-sm ${done ? "font-semibold text-green-900" : "font-medium"}`}>
                        {step.label}
                      </div>
                      {done && at && (
                        <div className="text-xs text-green-700 mt-0.5 font-mono">
                          {new Date(at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-neutral-500 mt-4 leading-relaxed">
              Auto-updates: logging a &ldquo;Meeting&rdquo; activity marks discovery done. Emailing any doc marks docs sent. Toggling &ldquo;Live&rdquo; also flips the client status to active.
            </p>
          </div>

          <div className="bg-white border border-neutral-200 p-6">
            <div className="overline mb-4">Onboarding documents</div>
            <p className="text-sm text-neutral-500 mb-4">
              Generate AI-drafted agreements and welcome docs for this client.
            </p>
            <div className="space-y-2 mb-6">
              {DOC_TYPES.map((d) => (
                <button
                  key={d.key}
                  data-testid={CLIENTS.generateDocBtn(d.key)}
                  onClick={() => generateDoc(d.key)}
                  disabled={genLoading === d.key}
                  className="w-full text-left px-4 py-3 border border-neutral-300 hover:border-black hover:bg-neutral-50 transition text-sm flex items-center justify-between disabled:opacity-50"
                >
                  <span className="flex items-center gap-2">
                    <FileText className="w-4 h-4" /> {d.label}
                  </span>
                  <span className="text-xs text-neutral-400">
                    {genLoading === d.key ? "Generating…" : "Generate →"}
                  </span>
                </button>
              ))}
            </div>

            <div className="overline mb-3">Generated</div>
            {documents.length === 0 ? (
              <p className="text-xs text-neutral-500">Nothing yet.</p>
            ) : (
              <div className="space-y-2">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="border border-neutral-200 hover:border-black transition"
                  >
                    <div className="px-3 py-2">
                      <div className="font-medium text-sm flex items-center gap-2 flex-wrap">
                        {doc.label}
                        {doc.signature?.signed_at && (
                          <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 bg-green-50 border border-green-200 text-green-800 font-mono uppercase tracking-wider" data-testid={`client-signed-badge-${doc.id}`}>
                            <CheckCircle2 className="w-3 h-3" /> Signed
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-neutral-500 font-mono mt-0.5">
                        {new Date(doc.created_at).toLocaleDateString()}
                        {doc.last_emailed_to && (
                          <span className="ml-2 text-green-700">· sent → {doc.last_emailed_to}</span>
                        )}
                      </div>
                      {doc.signature?.signed_at && (
                        <div className="text-xs text-green-800 mt-0.5">
                          Signed by {doc.signature.full_name} · {new Date(doc.signature.signed_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <div className="flex border-t border-neutral-100">
                      <a
                        data-testid={CLIENTS.docLink(doc.id)}
                        href={`${backendUrl}/api/onboarding/view/${doc.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-1 text-center py-2 text-xs font-medium hover:bg-neutral-50 transition inline-flex items-center justify-center gap-1"
                      >
                        View <ExternalLink className="w-3 h-3" />
                      </a>
                      <a
                        href={`${backendUrl}/api/onboarding/pdf/${doc.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-1 text-center py-2 text-xs font-medium hover:bg-neutral-50 transition inline-flex items-center justify-center gap-1 border-l border-neutral-100"
                      >
                        PDF <Download className="w-3 h-3" />
                      </a>
                      <button
                        data-testid={`client-email-doc-btn-${doc.id}`}
                        onClick={() => openEmailDoc(doc)}
                        className="flex-1 text-center py-2 text-xs font-medium hover:bg-neutral-50 transition inline-flex items-center justify-center gap-1 border-l border-neutral-100"
                      >
                        Email <Send className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Email doc modal */}
      {emailDoc && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          data-testid="client-email-doc-modal"
          onClick={() => setEmailDoc(null)}
        >
          <div
            className="bg-white border border-neutral-200 w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between">
              <div>
                <div className="overline">Email doc</div>
                <div className="text-lg font-bold mt-1">{emailDoc.label}</div>
                <div className="text-xs text-neutral-500 mt-0.5">to {client.business_name}</div>
              </div>
              <button
                onClick={() => setEmailDoc(null)}
                data-testid="client-email-doc-modal-close"
                className="text-neutral-400 hover:text-black"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <Field label="Send to email">
                <input
                  data-testid="client-email-doc-to-input"
                  type="email"
                  value={emailForm.to}
                  onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                  placeholder="client@example.com"
                  className="lf-input"
                />
              </Field>
              <Field label="Personal note (optional)">
                <textarea
                  data-testid="client-email-doc-intro-input"
                  value={emailForm.intro}
                  onChange={(e) => setEmailForm({ ...emailForm, intro: e.target.value })}
                  rows={4}
                  placeholder="Great meeting you today. Here's the agreement we discussed…"
                  className="lf-input"
                />
              </Field>
            </div>
            <div className="px-6 py-4 border-t border-neutral-100 flex justify-end gap-2">
              <button onClick={() => setEmailDoc(null)} className="lf-btn-outline text-sm">Cancel</button>
              <button
                data-testid="client-email-doc-send-btn"
                onClick={sendEmailDoc}
                disabled={emailSending || !emailForm.to}
                className="lf-btn-primary text-sm flex items-center gap-2"
              >
                <Mail className="w-4 h-4" />
                {emailSending ? "Sending…" : "Send email"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
